import React, { useEffect, useMemo, useState } from "react";
import {
  Typography,
  Box,
  Button,
  Grid,
  TextField,
  MenuItem,
  IconButton,
  Chip,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Pagination,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  InputAdornment,
} from "@mui/material";
import {
  useDeleteAdminExamMutation,
  useGetPartsQuery,
  useUpdateAdminExamMutation,
  useCreateExamMutation,
  useLazyGetAdminExamsQuery,
} from "@/store/api/adminApi";
import { useDeleteExamMutation } from "@/store/api/contentApi";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import QuestionIcon from "@mui/icons-material/Quiz";
import EmptyIcon from "@mui/icons-material/InboxOutlined";
import ExamForm from "./ExamForm";
import PartEditDialog from "../PartManagement/PartEditDialog";
import ExamAssignmentDialog from "@/components/admin/ExamAssignmentDialog";
import {
  levelColors,
  typeColors,
} from "@/constants/examConstants";
import ExamQuestionManagement from "./ExamQuestionManagement";
import { useGetExamQuestionsQuery } from "@/store/api/contentApi";
import { useDispatch } from "react-redux";
// import { setCurrentQuestions } from "@/store/slices/examSlice";
import { ArrowDownward, ExpandLess, ExpandMore } from "@mui/icons-material";
import SearchIcon from '@mui/icons-material/Search';
import useDebounce from "@/hooks/useDebounce";
import { ExportPdfButton } from "@/components/common";
import { downloadExamPdf } from "@/utils/pdfExport";

const ExamManagement = () => {
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExam, setEditingExam] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [examToDelete, setExamToDelete] = useState(null);
  const [questionManagement, setQuestionManagement] = useState({
    open: false,
    exam: null,
    part: null,
  });
  const [editingPart, setEditingPart] = useState(null);
  const [partEditDialogOpen, setPartEditDialogOpen] = useState(false);
  const [expandedExams, setExpandedExams] = useState({});
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [examToAssign, setExamToAssign] = useState(null);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    level: "",
    totalDuration: "",
    passingScore: 70,
    isPublic: true,
    allowGuests: false,
    audioFile: null,
    audioUrl: "",
  });

  const [getAdminExams, { data, isLoading, error, refetch }] = useLazyGetAdminExamsQuery();
  const [deleteExam, { isLoading: isDeleting }] = useDeleteAdminExamMutation();
  const [updateExam] = useUpdateAdminExamMutation();
  const [createExam, { isLoading: isCreating }] = useCreateExamMutation();

  const { data: partsData } = useGetPartsQuery();

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [filters, setFilters] = useState({
    search: "",
  });

  // Update filters when debounced search term changes
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      search: debouncedSearchTerm
    }));
  }, [debouncedSearchTerm]);

  // Fetch data when filters or page changes
  useEffect(() => {
    getAdminExams({ page, search: filters.search });
  }, [page, filters.search]);

  const handleFilterChange = (field, value) => {
    setPage(1);
    if (field === "search") {
      setSearchTerm(value);
      return;
    }
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleOpenDialog = (exam = null) => {
    if (exam) {
      setEditingExam(exam);
      setFormData({
        title: exam.title,
        description: exam.description,
        book: exam.book?._id || exam.book || "",
        level: exam.level,
        totalDuration: exam.totalDuration || "",
        passingScore: exam.passingScore,
        isPublic: exam.isPublic,
        allowGuests: exam.allowGuests || false,
        audioUrl: exam.audioUrl || "",
        audioFile: null,
      });
    } else {
      setEditingExam(null);
      setFormData({
        title: "",
        description: "",
        book: "",
        level: "",
        totalDuration: "",
        passingScore: 70,
        isPublic: true,
        allowGuests: false,
        audioFile: null,
        audioUrl: "",
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingExam(null);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmitSuccess = async (formData) => {
    try {
      const formDataToSend = new FormData();
      
      // Append all form fields to FormData
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined) {
          formDataToSend.append(key, formData[key]);
        }
      });

      if (editingExam) {
        await updateExam({ examId: editingExam._id, examData: formDataToSend }).unwrap();
      } else {
        await createExam(formDataToSend).unwrap();
      }

      handleCloseDialog();
      refetch();
    } catch (error) {
      console.error('Error saving exam:', error);
      // You might want to show an error message to the user here
    }
  };

  const handleDeleteClick = (exam) => {
    setExamToDelete(exam);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteExam(examToDelete._id).unwrap();
      setDeleteDialogOpen(false);
      setExamToDelete(null);
      refetch();
    } catch (err) {
      console.error("Failed to delete exam:", err);
    }
  };

  const handleOpenQuestionManagement = (exam, part) => {
    setQuestionManagement({ open: true, exam, part });
  };

  const handleOpenPartEditDialog = (part, e) => {
    e.stopPropagation();
    setEditingPart(part);
    setPartEditDialogOpen(true);
  };

  const toggleExamExpand = (examId) => {
    setExpandedExams((prev) => ({
      ...prev,
      [examId]: !prev[examId],
    }));
  };

  const handleClosePartEditDialog = () => {
    setPartEditDialogOpen(false);
    setEditingPart(null);
  };

  const handleOpenAssignmentDialog = (exam) => {
    setExamToAssign(exam);
    setAssignmentDialogOpen(true);
  };

  const handleCloseAssignmentDialog = () => {
    setAssignmentDialogOpen(false);
    setExamToAssign(null);
  };

  // useEffect(() => {
  //   // Please add icon for log questions
  //   if (questions) {
  //     dispatch(setCurrentQuestions(questions));
  //   }
  // }, [questions]);

  // const exams = data.data || [];
  // const pagination = data?.pagination || { totalPages: 1 };
  const exams = useMemo(() => {
    return data?.data
  }, [data])

  const pagination = useMemo(() => {
    return data?.pagination
  }, [data])

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load exams. Please try again.
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        p: 3,
      }}
    >
      <Typography
        variant="h4"
        gutterBottom
        sx={{ color: "#7b1fa2", fontWeight: "bold" }}
      >
        Examination contents management
      </Typography>

      {/* Filters and Actions */}
      <Box
        sx={{
          mb: 4,
          background: "rgba(255,255,255,0.9)",
          backdropFilter: "blur(20px)",
          borderRadius: 3,
          border: "1px solid rgba(255,255,255,0.3)",
          boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
        }}
      >
        <Grid sx={{ p: 3 }} container spacing={2} alignItems="center">
          <Grid item xs={12} sm={10}>
            <TextField
              fullWidth
              label="Search by title"
              value={searchTerm}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={2} sx={{ textAlign: "right" }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                background:
                  "linear-gradient(135deg, #7b1fa2 0%,rgb(123, 0, 245) 100%)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(172, 19, 238, 0.2)",
                borderRadius: 3,
                color: "white",
                fontWeight: 600,
                px: 3,
                py: 1.5,
                boxShadow: "0 4px 16px rgba(102, 126, 234, 0.3)",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 24px rgba(102, 126, 234, 0.4)",
                },
              }}
              onClick={() => handleOpenDialog()}
            >
              New Exam
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Exam List */}
      {exams?.length > 0 ? (
        <Grid container spacing={3}>
          {exams.map((exam) => (
            <Grid item xs={6} key={exam._id}>
              <Card
                sx={{
                  mb: 4,
                  background: "rgba(255,255,255,0.9)",
                  backdropFilter: "blur(20px)",
                  borderRadius: 3,
                  border: "1px solid rgba(255,255,255,0.3)",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Box>
                      <Typography variant="h6">{exam.title}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {exam.allowGuests && (
                        <Chip label="Guest" color="success" size="small" />
                      )}
                      <Chip label={`${exam.totalQuestions} questions`}  />
                      <ExportPdfButton
                        iconOnly
                        isAdmin
                        onExport={() => downloadExamPdf(exam._id, exam.title)}
                        onExportWithAnswers={() => downloadExamPdf(exam._id, exam.title, true)}
                        label="Export PDF"
                      />
                      <IconButton onClick={() => handleOpenDialog(exam)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteClick(exam)}>
                        <DeleteIcon sx={{ color: "#f44336" }} />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
                <CardContent>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {exam.description}
                  </Typography>
                  {exam.audioUrl && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Audio:
                      </Typography>
                      <audio
                        controls
                        src={exam.audioUrl}
                        style={{ width: "100%" }}
                      >
                        Your browser does not support the audio element.
                      </audio>
                    </Box>
                  )}
                  <Box sx={{ mt: 2 }}>
                    <Box
                      onClick={() => toggleExamExpand(exam._id)}
                      sx={{
                        cursor: "pointer",
                        fontWeight: 600,
                        borderBottom: "1px solid #ccc",
                        p: 1,
                        display: "flex",
                        justifyContent: "space-between",
                        transition: "all 0.3s ease",
                      }}
                    >
                      {expandedExams[exam._id] ? (
                        <>
                          Hide Questions
                          <ExpandLess sx={{ ml: 1, color: "#7b1fa2" }} />
                        </>
                      ) : (
                        <>
                          Show Questions
                          <ExpandMore sx={{ ml: 1, color: "#7b1fa2" }} />
                        </>
                      )}
                    </Box>
                    <Collapse
                      in={expandedExams[exam._id]}
                      timeout="auto"
                      unmountOnExit
                      sx={{ mt: 0 }}
                    >
                      <Box
                        sx={{ border: "1px solid #eee", borderRadius: 1, p: 2 }}
                      >
                        <List sx={{ width: "100%" }}>
                          {partsData?.data?.map((part) => (
                            <ListItem
                              key={part._id}
                              disablePadding
                              sx={{ "&:not(:last-child)": { mb: 1 } }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  width: "100%",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  p: 1,
                                  bgcolor: "grey.50",
                                  borderRadius: 1,
                                }}
                              >
                                <Typography variant="body1">
                                  Part {part.partNumber} - {part.title}
                                </Typography>
                                <Box sx={{ display: "flex", gap: 1, ml: 1 }}>
                                  <Button
                                    size="small"
                                    startIcon={<QuestionIcon />}
                                    sx={{
                                      borderColor: "transparent",
                                      color: "white",
                                      backgroundColor: "#2196F3",
                                      fontWeight: 600,
                                      textTransform: "none",
                                      borderRadius: 1,
                                      p: 1,
                                      minWidth: 100,
                                      transition: "all 0.3s ease",
                                      boxShadow:
                                        "rgba(0, 0, 0, 0.1) 0px 4px 12px",
                                      "&:hover": {
                                        backgroundColor: "#1976D2",
                                      },
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenQuestionManagement(exam, part);
                                    }}
                                  >
                                    Questions
                                  </Button>
                                </Box>
                              </Box>
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    </Collapse>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <EmptyIcon sx={{ fontSize: 80, color: "grey.400" }} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            No examinations found
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Get started by creating a new examination.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Create Exam
          </Button>
        </Box>
      )}

      {/* Pagination */}
      {pagination?.totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <Pagination
            count={pagination.totalPages || 1}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      {/* Exam Form Dialog */}
      <ExamForm
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSubmit={handleSubmitSuccess}
        formData={formData}
        onFormChange={handleFormChange}
        isSubmitting={isCreating}
        isEditing={!!editingExam}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{examToDelete?.title}"? This action
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Part Edit Dialog */}
      <PartEditDialog
        open={partEditDialogOpen}
        onClose={handleClosePartEditDialog}
        part={editingPart}
        onSuccess={() => {
          handleClosePartEditDialog();
          refetch();
        }}
      />

      {/* Question Management Dialog */}
      <Dialog
        open={questionManagement.open}
        onClose={() => setQuestionManagement({ open: false, exam: null })}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Typography sx={{ fontWeight: 600, fontSize: 20 }}>
            Manage Questions for: {questionManagement?.exam?.title}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {questionManagement.exam && (
            <ExamQuestionManagement
              examId={questionManagement.exam._id}
              examTitle={questionManagement.exam.title}
              part={questionManagement.part}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setQuestionManagement({ open: false, exam: null })}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Exam Assignment Dialog */}
      <ExamAssignmentDialog
        open={assignmentDialogOpen}
        onClose={handleCloseAssignmentDialog}
        exam={examToAssign}
      />
    </Box>
  );
};

// export { default } from './ExamManagement';

export default ExamManagement;
